import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from '@tanstack/react-router';
import { GripVertical, Edit, Copy, Trash2, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Question, Option } from '@/api/questions/getQuestions';

interface SortableQuestionsListProps {
  questions: Question[];
  onDuplicate?: (question: Question) => void;
  onDelete?: (question: Question) => void;
  onOrderChange?: (questionIds: string[]) => void;
}

function SortableRow({
  question,
  onDuplicate,
  onDelete,
  isExpanded,
  onToggleExpand,
}: {
  question: Question;
  onDuplicate?: (question: Question) => void;
  onDelete?: (question: Question) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.question_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasOptions = question.options && question.options.length > 0;
  const hasCorrectAnswer =
    question.question_type === 'tiebreaker'
      ? question.correct_value !== undefined && question.correct_value !== null
      : question.options?.some(opt => opt.is_correct === true);
  const canExpand = hasOptions || (question.question_type === 'tiebreaker' && hasCorrectAnswer);

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        className={`hover:bg-gray-50 ${isDragging ? 'bg-gray-100' : ''}`}
      >
        <td className="px-4 py-3 text-sm text-gray-500 w-8">
          <div
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="h-5 w-5" />
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 w-8">
          {canExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="text-gray-400 hover:text-gray-600"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900 flex-1">
          {question.question_text_en || 'Untitled Question'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 text-center">
          {hasCorrectAnswer ? (
            <Check className="h-4 w-4 text-green-600 inline-block" />
          ) : (
            <X className="h-4 w-4 text-red-600 inline-block" />
          )}
        </td>

        <td className="px-4 py-3 text-sm text-gray-600 text-right">
          <Badge variant="outline" className="text-xs">
            {question.points ?? 0} pts
          </Badge>
        </td>
        <td className="text-center">
          {(question.question_metadata as any)?.source?.eventName ?? '-'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="flex items-center gap-2 justify-end">
            <Link
              to="/dashboard/questions/$id"
              params={{ id: question.question_id }}
              search={{ pickem_id: question.pickem_id }}
            >
              <Button variant="ghost" size="sm">
                <Edit className="h-4 w-4" />
              </Button>
            </Link>
            {onDuplicate && (
              <Button variant="ghost" size="sm" onClick={() => onDuplicate(question)}>
                <Copy className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(question)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && canExpand && (
        <tr>
          <td colSpan={6} className="px-4 py-3 bg-gray-50">
            <div className="pl-12">
              {question.question_type === 'tiebreaker' ? (
                <>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Correct Answer:</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-700">{question.correct_value}</span>
                    <Badge variant="default" className="text-xs bg-green-600">
                      Correct
                    </Badge>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Options:</h4>
                  <div className="space-y-1">
                    {question.options!.map((option: Option, index: number) => (
                      <div
                        key={option.option_id || index}
                        className="flex items-center gap-2 text-sm"
                      >
                        {option.is_correct ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <X className="h-4 w-4 text-gray-400" />
                        )}
                        <span
                          className={
                            option.is_correct ? 'font-medium text-green-700' : 'text-gray-600'
                          }
                        >
                          {option.option_text_en || 'Untitled Option'}
                        </span>
                        {option.is_correct && (
                          <Badge variant="default" className="text-xs bg-green-600">
                            Correct
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function SortableQuestionsList({
  questions: initialQuestions,
  onDuplicate,
  onDelete,
  onOrderChange,
}: SortableQuestionsListProps) {
  const [questions, setQuestions] = useState(initialQuestions);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    setQuestions(initialQuestions);
  }, [initialQuestions]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setQuestions(items => {
        const oldIndex = items.findIndex(q => q.question_id === active.id);
        const newIndex = items.findIndex(q => q.question_id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Notify parent of the order change
        if (onOrderChange) {
          onOrderChange(newOrder.map(q => q.question_id));
        }

        return newOrder;
      });
    }
  };

  const toggleExpand = (questionId: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  if (questions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No questions yet. Add questions to start organizing them.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={questions.map(q => q.question_id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="border rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Question
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Has Answer
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Points
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Fixture
                  </th>

                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {questions.map(question => (
                  <SortableRow
                    key={question.question_id}
                    question={question}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    isExpanded={expandedQuestions.has(question.question_id)}
                    onToggleExpand={() => toggleExpand(question.question_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
